<template>
  <div>
    <div class="add-shipping-configuration__banner">
      <h4 class="add-shipping-configuration__header">
        Multiple Skus Per {{ displayAsPallet ? 'Pallet' : 'Box' }}
      </h4>
    </div>
    <sb-table
      :column-definitions="columnDefinitions"
      :rows="boxConfigurations"
    >
      <sb-table-row slot="sb-table-row" slot-scope="{row}">
        <!-- PRODUCT INFORMATION -->
        <sb-table-cell content-alignment="top">
          <div class="wro-multi-sku-table__sub-grid">
            <template v-for="skuConfig in row.skuConfigurations">
              <div
                :key="skuConfig.id"
                class="wro-multi-sku-table__sub-cell"
              >
                <sb-dropdown
                  class="wro-multi-sku-table__item wro-multi-sku-table__item--expand-width"
                  :position-fixed="true"
                  :options="filterSelectedProducts(row.id)"
                  :value="skuConfig.product"
                  @input="(val) => handleSelectProduct(row.id, skuConfig.id, val)"
                >
                  <sb-selected-option
                    slot="selection"
                    slot-scope="{ selected }"
                    v-title-from-text
                  >
                    {{ selected && selected.name }} {{ selected | resolveLotInfo }}
                  </sb-selected-option>
                  <sb-options>
                    <sb-option
                      slot-scope="{ option }"
                      :value="option"
                    >
                      <div class="wro-multi-sku-table__dropdown-item">
                        {{ option.name }}
                        <template v-if="option.isLotProduct">
                          <p class="wro-multi-sku-table__dropdown-item--supporting">
                            {{ option | resolveLotInfo }}
                          </p>
                        </template>
                      </div>
                    </sb-option>
                  </sb-options>
                </sb-dropdown>
              </div>
              <span
                v-if="boxConfigurations.length > 1 || row.skuConfigurations.length > 1"
                :key="skuConfig.id + 'remove'"
                class="wro-multi-sku-table__sub-cell-item wro-multi-sku-table__sub-cell-item--pad wro-multi-sku-table__sub-cell-item--center icon icon-trash"
                :style="{'margin-left': '10px'}"
                @click="handleRemoveConfiguration(row.id, skuConfig.id)"
              />
            </template>
          </div>
          <Variable :disabled="filterSelectedProducts(row.id).length === 0">
            <sb-anchor
              slot-scope="{ disabled }"
              class="wro-multi-sku-table__link wro-multi-sku-table__link--space-bottom-sm wro-multi-sku-table__link--space-top"
              :class="{'wro-multi-sku-table__link--disabled':disabled}"
              href="#"
              @click.prevent="() => { if (!disabled) handleAddConfiguration(row.id, row.skuConfigurations) }"
            >
              <span class="icon icon-plus-circle wro-multi-sku-table__icon wro-multi-sku-table__icon--large" :class="{'wro-multi-sku-table__icon--disabled':disabled}" />
              <p class="wro-multi-sku-table__text wro-multi-sku-table__text--small wro-multi-sku-table__text--space-left" :class="{'wro-multi-sku-table__text--disabled':disabled}">
                Add SKU to this configuration
              </p>
            </sb-anchor>
          </Variable>
        </sb-table-cell>
        <!-- SHIPMENT QUANTITY -->
        <sb-table-cell content-alignment="top">
          <div v-for="skuConfig in row.skuConfigurations" :key="skuConfig.id" class="wro-multi-sku-table__sub-cell">
            {{ extractShipmentQuantity(skuConfig.product) | dashNegatives }}
          </div>
        </sb-table-cell>
        <!-- QUANTITY PER BOX -->
        <sb-table-cell content-alignment="top">
          <div v-for="skuConfig in row.skuConfigurations" :key="skuConfig.id" class="wro-multi-sku-table__sub-cell">
            <sb-number-input
              class="wro-multi-sku-table__quantity-input"
              :value="skuConfig.quantityPerBox"
              :min="0"
              @input="(val) => handleSelectQuantityPerBox(row.id, skuConfig.id, val)"
            />
          </div>
        </sb-table-cell>
        <!-- NUMBER OF BOXES -->
        <sb-table-cell content-alignment="top">
          <div v-for="(skuConfig, index) in row.skuConfigurations" :key="skuConfig.id" class="wro-multi-sku-table__sub-cell">
            <sb-number-input
              v-if="index === 0"
              class="wro-multi-sku-table__quantity-input"
              :value="skuConfig.numberOfBoxes"
              :min="0"
              @input="(val) => handleNumberOfBoxes(row.id, skuConfig.id, val)"
            />
            <p v-else class="wro-multi-sku-table__text wro-multi-sku-table__text--pad wro-multi-sku-table__text--disabled">
              {{ skuConfig.numberOfBoxes }}
            </p>
          </div>
        </sb-table-cell>
        <!-- TOTAL QUANTITY PER CONFIGURATION -->
        <sb-table-cell content-alignment="top">
          <div v-for="skuConfig in row.skuConfigurations" :key="skuConfig.id" class="wro-multi-sku-table__sub-cell">
            {{ extractTotalQuantityPerConfiguration(skuConfig) | dashNegatives }}
          </div>
        </sb-table-cell>
        <!-- QUANTITY REMAINING -->
        <sb-table-cell content-alignment="top">
          <div
            v-for="skuConfig in row.skuConfigurations"
            :key="skuConfig.id"
            class="wro-multi-sku-table__sub-cell wro-multi-sku-table__quantity"
            :class="{ 'wro-multi-sku-table__quantity--error': skuConfig.product && extractQuantityRemaining(skuConfig) !== 0 }"
          >
            <template v-if="skuConfig.product">
              {{ extractQuantityRemaining(skuConfig) }}
            </template>
            <template v-else>
              {{ extractQuantityRemaining(skuConfig) | dashNegatives }}
            </template>
          </div>
        </sb-table-cell>
        <!-- Box Dimensions -->
        <sb-table-cell v-if="shouldIncludeDimensions" content-alignment="top">
          <template v-for="(skuConfig, index) in row.skuConfigurations">
            <div
              v-if="index===0"
              :key="skuConfig.id"
              class="wro-multi-sku-table__sub-cell box-dimensions-configuration"
            >
              <sb-field class="wro-multi-sku-table__quantity-dimensions" :variation="errorDimensionsVariation(skuConfig)">
                <sb-text-input
                  :value="skuConfig.length | displayValue"
                  append="L"
                  @input="(val) => handleChangeBoxDimensions(row.id, skuConfig.id, 'length', val, skuConfig)"
                />
              </sb-field> x
              <sb-field class="wro-multi-sku-table__quantity-dimensions" :variation="errorDimensionsVariation(skuConfig)">
                <sb-text-input
                  :value="skuConfig.width | displayValue"
                  append="W"
                  @input="(val) => handleChangeBoxDimensions(row.id, skuConfig.id, 'width', val, skuConfig)"
                />
              </sb-field> x
              <sb-field class="wro-multi-sku-table__quantity-dimensions" :variation="errorDimensionsVariation(skuConfig)">
                <sb-text-input
                  :value="skuConfig.height | displayValue"
                  append="H"
                  @input="(val) => handleChangeBoxDimensions(row.id, skuConfig.id, 'height', val, skuConfig)"
                />
              </sb-field>
            </div>
          </template>
        </sb-table-cell>
        <!-- Weight -->
        <sb-table-cell v-if="shouldIncludeDimensions" content-alignment="top">
          <template v-for="(skuConfig, index) in row.skuConfigurations">
            <div
              v-if="index===0"
              :key="skuConfig.id"
              class="wro-multi-sku-table__sub-cell box-weight-configuration"
            >
              <sb-field class="wro-multi-sku-table__quantity-dimensions" :variation="errorWeightVariation(skuConfig)">
                <sb-text-input
                  :value="(skuConfig.weight) ? skuConfig.weight.pounds : 0 | displayValue"
                  append="lb"
                  @input="(val) => handleChangeBoxWeight(row.id, skuConfig.id, 'pounds', val, skuConfig)"
                />
              </sb-field> x
              <sb-field class="wro-multi-sku-table__quantity-dimensions" :variation="errorWeightVariation(skuConfig)">
                <sb-text-input
                  :value="(skuConfig.weight) ? skuConfig.weight.ounces : 0 | displayValue"
                  append="oz"
                  @input="(val) => handleChangeBoxWeight(row.id, skuConfig.id, 'ounces', val, skuConfig)"
                />
              </sb-field>
            </div>
          </template>
        </sb-table-cell>
      </sb-table-row>
    </sb-table>
    <div class="wro-multi-sku-table__footer">
      <sb-button :disabled="isValidConfiguration" class="wro-multi-sku-table__button wro-multi-sku-table__button--space-right" @click="handleAddNewBoxConfiguration">
        Add new {{ displayAsPallet ? 'pallet' : 'box' }} configuration
      </sb-button>
      <p class="wro-multi-sku-table__text">
        Lot products with expiration dates must not be mixed in the same {{ displayAsPallet ? 'pallet' : 'box' }}.
        <help-center-link
          v-if="!isPoweredBySubAccountUser"
          link-key="createWro/lotItems"
        >
          Learn more.
        </help-center-link>
      </p>
    </div>
  </div>
</template>

<script>
import Variable from '@/components/utils/Variable'
import _differenceBy from 'lodash/differenceBy'
import { ModuleName } from '@/store/modules/createWarehouseReceivingOrder'
import {
  extractShipmentQuantityHelper,
  extractTotalQuantityPerConfigurationHelper,
  extractQuantityRemainingHelper,
  extractQuantitiesRemainingPerProduct,
  isValidMultiSkuHelper,
  buildProductIdentity,
  validateBoxDimensions,
  validateBoxWeight
} from './helper'

export default {
  name: 'MultiSkuConfigurationTable',
  inject: ['isPoweredBySubAccountUser'],
  components: {
    Variable
  },
  directives: {},
  filters: {
    dashNegatives (val) {
      return val < 0 ? '-' : val
    },
    resolveLotInfo (option) {
      const noVal = ''
      if (!option) return noVal
      const { lotInformation } = option
      if (!lotInformation) return noVal
      const lotInfo = [lotInformation.lotNumber, lotInformation.expirationDate]
        .filter(str => str.length > 0)
        .join(', ')
      return lotInfo.length > 0 ? ` - ${lotInfo}` : ''
    },
    displayValue (value) {
      return `${value}`
    }
  },
  props: {
    machine: {
      type: Object,
      required: true
    },
    shouldIncludeDimensions: {
      type: Boolean,
      required: true
    }
  },
  data: () => ({}),
  computed: {
    isValidConfiguration () {
      return (this.machine.currentStateName() || '').indexOf('valid') > -1
    },
    draft () {
      return this.$shipBobStore.getters[ModuleName + '/draft']
    },
    packagingTypeId () {
      return this.draft.packagingType.id
    },
    displayAsPallet () {
      return this.packagingTypeId === 2
    },
    columnDefinitions () {
      return [
        { name: 'Item Information', field: 'productInfo' },
        { name: 'Total Shipment Quantity Per SKU', field: 'shipmentQuantity' },
        { name: (this.displayAsPallet ? 'Quantity per pallet' : 'Quantity per box'), field: 'quantityPerBox' },
        { name: (this.displayAsPallet ? '# of Pallets Per Configuration' : '# of Boxes Per Configuration'), field: 'numberOfBoxes' },
        { name: 'Total Quantity Per Configuration', field: 'totalQuantity' },
        { name: 'Quantity Remaining', field: 'quantityRemaining' },
        ...(this.shouldIncludeDimensions
          ? [
              { name: 'Box Dimensions (in inches)', field: 'boxDimensions' },
              { name: 'Weight', field: 'boxWeight' }
            ]
          : [])
      ]
    },
    selectedProducts () {
      return this.$shipBobStore.getters[ModuleName + '/selectedProductsWithLotInformation']
    },
    boxConfigurations () {
      return this.$shipBobStore.getters[ModuleName + '/multiSkuShippingConfigurations']
    }
  },
  watch: {
    boxConfigurations: {
      handler: function () {
        this.validateConfigurations()
      },
      deep: true
    }
  },
  beforeCreate () {},
  created () {},
  beforeMount () {},
  mounted () {},
  beforeDestroy () {},
  destroyed () {},
  beforeUpdate () {},
  updated () {},
  methods: {
    filterSelectedProducts (boxConfigId) {
      const boxIndex = this.boxConfigurations.findIndex(bc => bc.id === boxConfigId)
      if (boxIndex <= -1) return this.selectedProducts
      const addedProducts = this.boxConfigurations[boxIndex].skuConfigurations.map(sc => sc.product).filter(p => p !== null)
      const quantitiesRemaining = extractQuantitiesRemainingPerProduct({
        selectedProducts: this.selectedProducts,
        boxConfigurations: this.boxConfigurations
      }).filter(qr => qr.remaining === 0).map(qr => qr.product)
      return _differenceBy(_differenceBy(this.selectedProducts, addedProducts, p => p.id), quantitiesRemaining, p => buildProductIdentity(p))
    },
    handleSelectProduct (boxConfigId, skuConfigId, product) {
      this.$shipBobStore.dispatch(ModuleName + '/selectProductForMultiSkuConfigurationById', { boxConfigId, skuConfigId, product })
    },
    extractShipmentQuantity (product) {
      return extractShipmentQuantityHelper(this.selectedProducts, product)
    },
    handleSelectQuantityPerBox (boxConfigId, skuConfigId, value) {
      this.$shipBobStore.dispatch(ModuleName + '/selectQuantityForMultiSkuConfigurationById', { boxConfigId, skuConfigId, value })
    },
    handleNumberOfBoxes (boxConfigId, skuConfigId, value) {
      this.$shipBobStore.dispatch(ModuleName + '/selectNumberOfBoxesForMultiSkuConfigurationById', { boxConfigId, skuConfigId, value })
    },
    handleChangeBoxDimensions (boxConfigId, skuConfigId, prop, value, skuConfig) {
      const parsedValue = this.sanitizeNumber(value)
      skuConfig[prop] = parsedValue
      this.$shipBobStore.dispatch(ModuleName + '/updateMultiSKUConfiguration', { boxConfigId, skuConfigId, skuConfig })
    },
    handleChangeBoxWeight (boxConfigId, skuConfigId, prop, value, skuConfig) {
      const parsedValue = this.sanitizeNumber(value)
      skuConfig.weight[prop] = parsedValue
      this.$shipBobStore.dispatch(ModuleName + '/updateMultiSKUConfiguration', { boxConfigId, skuConfigId, skuConfig })
    },
    sanitizeNumber (value) {
      const parsedNumber = parseInt(value)
      return isNaN(parsedNumber) ? 0 : parsedNumber
    },
    extractTotalQuantityPerConfiguration (configuration) {
      return extractTotalQuantityPerConfigurationHelper(configuration)
    },
    extractQuantityRemaining (configuration) {
      return extractQuantityRemainingHelper(this.selectedProducts, this.boxConfigurations, configuration)
    },
    handleAddConfiguration (boxConfigId) {
      this.$shipBobStore.dispatch(ModuleName + '/addSkuConfigurationById', boxConfigId)
    },
    handleRemoveConfiguration (boxConfigId, skuConfigId) {
      this.$shipBobStore.dispatch(ModuleName + '/removeSkuConfigurationById', { boxConfigId, skuConfigId })
    },
    handleAddNewBoxConfiguration () {
      this.$shipBobStore.dispatch(ModuleName + '/addMultiSkuBoxConfiguration')
    },
    validateConfigurations () {
      const valid = isValidMultiSkuHelper({
        selectedProducts: this.selectedProducts,
        boxConfigurations: this.boxConfigurations
      }, this.shouldIncludeDimensions)

      if (valid) {
        this.machine.transition('ALL_QUANTITIES_ADDED')
      } else {
        this.machine.transition('INVALID_CONFIGURATION')
      }
    },
    errorDimensionsVariation (skuConfig) {
      return (validateBoxDimensions(skuConfig)) ? 'default' : 'error'
    },
    errorWeightVariation (skuConfig) {
      return (validateBoxWeight(skuConfig)) ? 'default' : 'error'
    }
  },
  i18n: { messages: {} }
}
</script>

<style lang='scss'>
@import '../../../../../node_modules/shipbob-design-system/dist/system/tokens/tokens';

.wro-multi-sku-table {
  &__sub-grid {
    display: grid;
    grid-template-columns: 88% 12%;
  }
  &__sub-cell {
    min-height: 2.5rem;
    display: flex;
    align-items: center;
    &:nth-child(n + 2) {
      margin-top: 1rem;
    }
  }
  &__sub-cell-item {
    display: flex;
    cursor: pointer;
    &--pad {
      margin-left: 0.75rem;
      padding: 0.5rem;
    }
    &--center {
      align-items: center;
    }
    &:nth-child(n + 4) {
      margin-top: 1rem;
    }
  }
  &__item {
    &--expand-width {
      width: 100%;
    }
  }
  &__quantity {
    &--error {
      color: #ff0000;
    }
  }
  &__quantity-input {
    width: 8rem;
  }
  &__quantity-dimensions {
    width: 4.5rem;
    padding-right: 5px;
    padding-left: 5px;
  }
  &__link {
    display: flex;
    align-items: center;
    font-weight: 600;
    &--space-top {
      margin-top: 1rem;
    }
    &--space-bottom-sm {
      margin-bottom: 0.25rem;
    }
    &--disabled {
      cursor: default !important;
      color: $color-disabled;
    }
  }
  &__icon {
    font-weight: 600;
    cursor: pointer;
    &--space-sides {
      padding: 0 1rem;
    }
    &--large {
      font-size: 1.25rem;
    }
    &--disabled {
      cursor: default;
      color: $color-disabled;
    }
  }
  &__button {
    &--space-right {
      margin-right: 1rem;
    }
  }
  &__text {
    margin: 0;
    font-weight: inherit;
    &--disabled {
      color: $color-disabled;
      cursor: default;
    }
    &--small {
      font-size: 0.75rem;
    }
    &--space-left {
      margin-left: 0.25rem;
    }
    &--pad {
      padding: .6em .7em .55em;
    }
  }
  &__footer {
    margin: 2rem;
    display: flex;
    align-items: center;
    flex-wrap: wrap;
  }
  &__dropdown-item {
    display: flex;
    &--supporting {
      margin-left: 0.25rem;
      font-weight: 300;
      margin-bottom: 0;
    }
  }
}
</style>
